<template>
  <div class="row">
    <div class="col-12">
      <card class="custom-shadow-card-header">
        <div class="row">
          <div class="col-12 col-md-5 mb-3">
            <label>{{ $t('common_words.farm') }} *</label>
            <el-select
              v-model="form.farm_id"
              clearable
              filterable
              class="select-primary custom-width-select required-farm shadow-sm"
              size="large"
              :placeholder="`${$t('common_words.select')}...`"
              @change="resetCapture"
            >
              <el-option
                v-for="farm in farms"
                :key="farm.id"
                class="select-primary"
                :value="farm.id"
                :label="farm.name"
              />
            </el-select>
          </div>
          <div class="col-12 col-md-4 mb-3">
            <label>{{ $t('common_words.date') }} *</label>
            <br />
            <el-date-picker
              v-model="form.date"
              type="date"
              class="shadow-sm required-date"
              :picker-options="pickerOptions"
              :placeholder="`${$t('common_words.select2')}...`"
              @change="resetCapture"
            />
          </div>

          <div class="col-12 col-sm-12 col-md-12 col-lg-12 col-xl-8">
            <div class="row custom-mt-30">
              <div class="col-12 col-sm-6 col-md-3 col-lg-3 col-xl-3 mb-2">
                <l-button
                  wide
                  type="info"
                  class="btn-filter"
                  @click="generate"
                  style="width: inherit"
                >
                  {{ $t('common_words.generate') }}
                </l-button>
              </div>
            </div>
          </div>
        </div>
      </card>

      <div v-if="messageAlert" class="alert alert-warning" role="alert">
        {{ messageAlert }}
      </div>

      <card
        v-if="showTable"
        class="custom-shadow-card-header custom-table-responsive"
        :title="$t('irrigation.blood_pressure_monitors_capture')"
      >
        <div class="mt-4 px-3">
          <div class="row" v-if="isIndex() && (isCreate() || isUpdate())">
            <div class="table-responsive">
              <table class="table table-striped table-bordered">
                <thead class="cusmtom-thead-color">
                  <tr class="cusmtom-thead-color">
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('common_words.section') }}
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('irrigation.block') }}
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('common_words.variety') }}
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('common_words.bed') }}
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('common_words.valve') }}
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('devices.blood_pressure_monitors') }}
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('common_words.value') }} (kPa)
                    </th>
                    <th class="text-center p-3 custom-header-label">
                      {{ $t('common_words.options') }}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(row, index) in form.details" :key="row.key">
                    <td>
                      <el-select
                        v-model="row.section_id"
                        clearable
                        filterable
                        class="select-primary"
                        :placeholder="`${$t('common_words.select')}...`"
                        @change="onSectionChange(index)"
                      >
                        <el-option
                          v-for="section in sections"
                          :key="section.id"
                          class="select-primary"
                          :value="section.id"
                          :label="section.name"
                        />
                      </el-select>
                    </td>
                    <td>
                      <el-select
                        v-model="row.block_id"
                        clearable
                        filterable
                        class="select-primary"
                        :disabled="!row.section_id"
                        :placeholder="`${$t('common_words.select')}...`"
                        @change="onBlockChange(index)"
                      >
                        <el-option
                          v-for="block in blocksByRow(index)"
                          :key="block.id"
                          class="select-primary"
                          :value="block.id"
                          :label="block.name"
                        />
                      </el-select>
                    </td>
                    <td>
                      <el-select
                        v-model="row.crop_variety_id"
                        clearable
                        filterable
                        class="select-primary"
                        :disabled="!row.block_id"
                        :placeholder="`${$t('common_words.select')}...`"
                        @change="onVarietyChange(index)"
                      >
                        <el-option
                          v-for="variety in varietiesByRow(index)"
                          :key="variety.id"
                          class="select-primary"
                          :value="variety.id"
                          :label="variety.name"
                        />
                      </el-select>
                    </td>
                    <td>
                      <el-select
                        v-model="row.bed_id"
                        clearable
                        filterable
                        class="select-primary"
                        :disabled="!row.crop_variety_id"
                        :placeholder="`${$t('common_words.select')}...`"
                        @change="onBedChange(index)"
                      >
                        <el-option
                          v-for="bed in bedsByRow(index)"
                          :key="bed.id"
                          class="select-primary"
                          :value="bed.id"
                          :label="bed.name"
                        />
                      </el-select>
                    </td>
                    <td>
                      <el-select
                        v-model="row.valve_id"
                        clearable
                        filterable
                        class="select-primary"
                        :disabled="!row.bed_id"
                        :placeholder="`${$t('common_words.select')}...`"
                        @change="onValveChange(index)"
                      >
                        <el-option
                          v-for="valve in valvesByRow(index)"
                          :key="valve.id"
                          class="select-primary"
                          :value="valve.id"
                          :label="valve.name"
                        />
                      </el-select>
                    </td>
                    <td>
                      <el-select
                        v-model="row.blood_pressure_monitor_id"
                        clearable
                        filterable
                        class="select-primary"
                        :disabled="!row.valve_id"
                        :placeholder="`${$t('common_words.select')}...`"
                      >
                        <el-option
                          v-for="monitor in monitorsByRow(index)"
                          :key="monitor.id"
                          class="select-primary"
                          :value="monitor.id"
                          :label="monitor.name"
                        />
                      </el-select>
                    </td>
                    <td class="value-cell">
                      <fg-input
                        v-model.number="row.value"
                        type="number"
                        step="0.01"
                        class="mb-0"
                        v-validate="modelValidations.value"
                        :name="`common_words.value_${index}`"
                        :error="
                          getFieldErrorByIndex(
                            'common_words.value',
                            true,
                            index
                          )
                        "
                      />
                    </td>
                    <td
                      class="text-center custom-td-pd"
                      style="min-width: 120px"
                    >
                      <div class="d-flex justify-content-center mt-3">
                        <a
                          v-if="isUpdate()"
                          v-tooltip.top-center="$t('common_words.add')"
                          class="btn-success btn-simple btn-link custom-margin-actions"
                          @click.prevent="addRow(index)"
                        >
                          <em class="fa fa-plus custom-size-fa-actions"></em>
                        </a>
                        <a
                          v-if="form.details.length > 1 && isDelete()"
                          v-tooltip.top-center="$t('common_words.erase')"
                          class="btn-danger btn-simple btn-link custom-margin-actions"
                          @click.prevent="removeRow(index)"
                        >
                          <em class="fa fa-times custom-size-fa-actions"></em>
                        </a>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <hr />

          <div class="row">
            <div class="col-6 text-right" v-if="isCreate() || isUpdate()">
              <l-button wide type="info" class="btn-filter m-4" @click="save">
                {{ $t('common_words.save') }}
              </l-button>
            </div>

            <div class="col-6 text-left" v-if="isDelete()">
              <l-button
                wide
                type="danger"
                class="btn-filter m-4"
                @click="deleteCapture"
              >
                {{ $t('common_words.delete') }}
              </l-button>
            </div>
          </div>
        </div>
      </card>
    </div>
  </div>
</template>

<script>
import { Axios } from 'src/class/Axios.js';
import moment from 'moment';
import SweetAlert from 'src/class/Alerts';
import MixinAlert from 'src/mixins/mixin-alert';
import MixinForm from 'src/mixins/forms/mixin-form';
import MixinLoading from 'src/mixins/mixin-loading';
import mixinMasters from 'src/util/mixinMasters';
import MixinMenu from 'src/util/mixinMenu';
import MixinPermissions from 'src/util/mixinPermissions';
import mixinLocation from 'src/util/mixinLocation';
import { isset } from 'src/util/Util.js';

export default {
  mixins: [
    MixinAlert,
    MixinForm,
    MixinLoading,
    mixinMasters,
    MixinMenu,
    MixinPermissions,
    mixinLocation,
  ],
  data() {
    return {
      farms: [],
      farmConfiguration: null,
      monitors: [],
      location: null,
      headerId: null,
      showTable: false,
      rowValidationError: false,
      nextRowKey: 1,
      pickerOptions: {
        disabledDate(time) {
          return time.getTime() > Date.now();
        },
      },
      form: {
        farm_id: null,
        date: new Date(moment().format('YYYY-MM-DD').replace(/-/, '/')),
        details: [],
      },
      modelValidations: {
        value: { required: true, decimal: 2 },
      },
      messageAlert: null,
    };
  },
  computed: {
    relations() {
      if (!this.farmConfiguration) return [];
      const relations =
        this.farmConfiguration.sections_rel ||
        this.farmConfiguration.sections ||
        [];
      return Array.isArray(relations)
        ? relations.filter((relation) => relation && !relation.deleted_at)
        : [];
    },
    sections() {
      return this.uniqueOptions(
        this.relations.map((relation) => this.sectionOption(relation))
      );
    },
  },
  methods: {
    emptyRow() {
      return {
        key: this.nextRowKey++,
        id: null,
        section_id: null,
        block_id: null,
        crop_variety_id: null,
        bed_id: null,
        valve_id: null,
        blood_pressure_monitor_id: null,
        value: null,
        latitude: null,
        longitude: null,
        altitude: null,
      };
    },

    async getLocation() {
      const locationData = await this.getPosition();
      return {
        lat: locationData.lat,
        lng: locationData.lng,
        alt: locationData.alt,
      };
    },

    async setLocation() {
      try {
        this.location = await this.getLocation();
        this.form.details.forEach((detail) => {
          detail.latitude = this.location.lat;
          detail.longitude = this.location.lng;
          detail.altitude = this.location.alt;
        });
      } catch (error) {
        console.error('No se pudo obtener la ubicación:', error);
        SweetAlert.warning('common_words.geolocation_could_not_be_obtained');
      }
    },

    option(id, name) {
      return isset(id) && name ? { id, name } : null;
    },

    sectionOption(relation) {
      const section = relation.section || {};
      return this.option(
        relation.section_id || section.id || relation.id,
        relation.name || section.name
      );
    },

    varietyOption(relation) {
      const variety =
        relation.cropVariety || relation.crop_variety || relation.variety || {};
      return this.option(
        relation.crop_variety_id || variety.id,
        relation.crop_variety_name || variety.name
      );
    },

    bedOption(relation) {
      const bed = relation.bed || {};
      return this.option(
        relation.bed_id || bed.id,
        relation.bed_name || bed.name
      );
    },

    valveOption(relation) {
      const valve = relation.valve || {};
      return this.option(
        relation.valve_id || valve.id,
        relation.valve_name || valve.name
      );
    },

    uniqueOptions(options) {
      const unique = [];
      options.filter(Boolean).forEach((option) => {
        if (!unique.some((item) => item.id == option.id)) unique.push(option);
      });
      return unique;
    },

    relationMatchesSection(relation, sectionId) {
      const section = this.sectionOption(relation);
      return section && section.id == sectionId;
    },

    relationHasBlock(relation, blockId) {
      const blocks = Array.isArray(relation.blocks) ? relation.blocks : [];
      return blocks.some((block) => {
        const option =
          typeof block === 'string'
            ? this.option(block, block)
            : this.option(block.id || block.block_id, block.name);
        return option && option.id == blockId;
      });
    },

    relationsByRow(index) {
      const row = this.form.details[index];
      return this.relations.filter(
        (relation) =>
          this.relationMatchesSection(relation, row.section_id) &&
          (!row.block_id || this.relationHasBlock(relation, row.block_id)) &&
          (!row.crop_variety_id ||
            (this.varietyOption(relation) &&
              this.varietyOption(relation).id == row.crop_variety_id)) &&
          (!row.bed_id ||
            (this.bedOption(relation) &&
              this.bedOption(relation).id == row.bed_id))
      );
    },

    blocksByRow(index) {
      const row = this.form.details[index];
      const blocks = this.relations
        .filter((relation) =>
          this.relationMatchesSection(relation, row.section_id)
        )
        .reduce((result, relation) => {
          const relationBlocks = Array.isArray(relation.blocks)
            ? relation.blocks
            : [];
          return result.concat(
            relationBlocks.map((block) =>
              typeof block === 'string'
                ? this.option(block, block)
                : this.option(block.id || block.block_id, block.name)
            )
          );
        }, []);
      return this.uniqueOptions(blocks);
    },

    varietiesByRow(index) {
      return this.uniqueOptions(
        this.relationsByRow(index).map((relation) =>
          this.varietyOption(relation)
        )
      );
    },

    bedsByRow(index) {
      return this.uniqueOptions(
        this.relationsByRow(index).map((relation) => this.bedOption(relation))
      );
    },

    valvesByRow(index) {
      return this.uniqueOptions(
        this.relationsByRow(index).map((relation) => this.valveOption(relation))
      );
    },

    monitorsByRow(index) {
      const valveId = this.form.details[index].valve_id;
      return this.monitors.filter((monitor) => {
        const monitorValveId =
          monitor.valve_id || (monitor.valve && monitor.valve.id);
        return monitorValveId == valveId;
      });
    },

    onSectionChange(index) {
      Object.assign(this.form.details[index], {
        block_id: null,
        crop_variety_id: null,
        bed_id: null,
        valve_id: null,
        blood_pressure_monitor_id: null,
      });
    },

    onBlockChange(index) {
      Object.assign(this.form.details[index], {
        crop_variety_id: null,
        bed_id: null,
        valve_id: null,
        blood_pressure_monitor_id: null,
      });
    },

    onVarietyChange(index) {
      Object.assign(this.form.details[index], {
        bed_id: null,
        valve_id: null,
        blood_pressure_monitor_id: null,
      });
    },

    onBedChange(index) {
      Object.assign(this.form.details[index], {
        valve_id: null,
        blood_pressure_monitor_id: null,
      });
    },

    onValveChange(index) {
      this.form.details[index].blood_pressure_monitor_id = null;
    },

    addRow(index) {
      this.form.details.splice(index + 1, 0, this.emptyRow());
    },

    removeRow(index) {
      this.form.details.splice(index, 1);
    },

    cleanAll() {
      this.showTable = false;
      this.headerId = null;
      this.farmConfiguration = null;
      this.monitors = [];
      this.form.details = [];
      this.rowValidationError = false;
      this.location = null;
      this.nextRowKey = 1;
      this.$validator.reset();
    },

    resetCapture() {
      this.cleanAll();
    },

    normalizeData(response) {
      return response && response.data ? response.data.data : null;
    },

    buildRow(detail) {
      return {
        key: this.nextRowKey++,
        id: detail.id || null,
        section_id:
          detail.section_id || (detail.section && detail.section.id) || null,
        block_id: detail.block_id || (detail.block && detail.block.id) || null,
        crop_variety_id:
          detail.crop_variety_id ||
          (detail.crop_variety && detail.crop_variety.id) ||
          null,
        bed_id: detail.bed_id || (detail.bed && detail.bed.id) || null,
        valve_id: detail.valve_id || (detail.valve && detail.valve.id) || null,
        blood_pressure_monitor_id:
          detail.blood_pressure_monitor_id ||
          (detail.blood_pressure_monitor && detail.blood_pressure_monitor.id) ||
          null,
        value: detail.value,
        latitude: detail.latitude || null,
        longitude: detail.longitude || null,
        altitude: detail.altitude || null,
      };
    },

    generate() {
      if (!isset(this.form.farm_id)) {
        return this.validationInputsRequired('farm');
      }
      if (!this.form.date) return this.validationInputsRequired('date');

      this.showHideLoading(true);
      const date = moment(this.form.date).format('YYYY-MM-DD');
      Promise.all([
        Axios.getMasters(`farms/farms/configuration/${this.form.farm_id}`),
        Axios.getMasters(
          `devices/bloodpressuremonitors?farm_id=${this.form.farm_id}`
        ),
        Axios.getMasters(
          `irrigation/catchbloodpressuremonitors/${date}/${this.form.farm_id}`
        ),
      ])
        .then(([configurationResponse, monitorsResponse, captureResponse]) => {
          this.farmConfiguration =
            this.normalizeData(configurationResponse) || {};

          this.monitors = this.normalizeData(monitorsResponse) || [];

          const capture = this.normalizeData(captureResponse);

          const details =
            capture && Array.isArray(capture.details) ? capture.details : [];

          this.headerId = capture
            ? capture.header_id || capture.id || null
            : null;

          this.form.details = details.length
            ? details.map((detail) => this.buildRow(detail))
            : [this.emptyRow()];

          this.showTable = true;
          this.showHideLoading();
        })
        .catch((error) => {
          this.showHideLoading();
          this.showMessageErrors(error);
        });
    },

    rowIsValid(row) {
      return (
        isset(row.section_id) &&
        isset(row.block_id) &&
        isset(row.crop_variety_id) &&
        isset(row.bed_id) &&
        isset(row.valve_id) &&
        isset(row.blood_pressure_monitor_id) &&
        row.value !== null &&
        row.value !== undefined &&
        row.value !== ''
      );
    },

    deleteCapture() {
      if (!this.headerId) {
        SweetAlert.warning(this.$t('production.no_record_to_delete'));
        return;
      }

      SweetAlert.delete(this.$t('production.capture_details')).then(
        (result) => {
          if (!result.value) return;

          this.showHideLoading(true);
          Axios.destroyMaster({
            id: this.headerId,
            route: 'irrigation/catchbloodpressuremonitors/delete',
          })
            .then((response) => {
              this.showHideLoading();
              this.cleanAll();
              SweetAlert.success(response.data.message);
            })
            .catch((error) => {
              this.showHideLoading();
              this.showMessageErrors(error);
            });
        }
      );
    },

    save() {
      this.$validator.validateAll().then(async (valid) => {
        this.rowValidationError = this.form.details.some(
          (row) => !this.rowIsValid(row)
        );
        if (!valid || this.rowValidationError) return;

        try {
          await this.setLocation();
        } catch (error) {
          SweetAlert.warning(
            this.$t('common_words.geolocation_could_not_be_obtained')
          );
          return;
        }

        const data = {
          route: 'irrigation/catchbloodpressuremonitors',
          id: this.headerId,
          farm_id: this.form.farm_id,
          date_save: moment(this.form.date).format('YYYY-MM-DD'),
          user_id: this.$store.state.auth.user.id,
          details: this.form.details.map((row) => ({
            id: row.id,
            section_id: row.section_id,
            block_id: row.block_id,
            crop_variety_id: row.crop_variety_id,
            bed_id: row.bed_id,
            valve_id: row.valve_id,
            blood_pressure_monitor_id: row.blood_pressure_monitor_id,
            value: row.value,
            latitude: String(row.latitude),
            longitude: String(row.longitude),
            altitude: String(row.altitude || 0),
          })),
        };
        const request = this.headerId
          ? Axios.updateMaster(data)
          : Axios.postMasters(data);

        this.showHideLoading(true);
        request
          .then((response) => {
            this.showHideLoading();
            this.cleanAll();
            SweetAlert.success(response.data.message);
          })
          .catch((error) => {
            this.showHideLoading();
            this.showMessageErrors(error);
          });
      });
    },
  },

  created() {
    this.showHideLoading(true);
    Axios.getMasters('farms/farms/only')
      .then((response) => {
        this.farms = response.data.data;
        this.showHideLoading();
      })
      .catch((error) => {
        this.showHideLoading();
        this.showMessageErrors(error);
      });
  },

  mounted() {
    this.selectedItemOnMenu('irrigation');
  },

  destroyed() {
    this.deselectedItemOnMenu('irrigation');
  },
};
</script>

<style scoped>
::v-deep .card-title {
  font-weight: 400;
}

.card .table:not(.not-last-td) thead th:last-child {
  display: revert !important;
}

.custom-td-pd {
  padding: 0px !important;
}

.table-responsive {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  max-height: 75vh;
}

.cusmtom-thead-color {
  position: sticky;
  top: 0;
  z-index: 2;
}
</style>
